import { useMemo, useRef, useState } from 'react';
import { useRobotRevisions } from '../../../hooks/useRevisions.ts';
import { useBringupContent } from '../../../hooks/useBringup.ts';
import { buildInstallBlock } from './installBlock.ts';
import {
  Dialog, DialogSection, CopyButton, useDismissGuard,
} from './NewRobotDialog.tsx';
import { Button, IconButton } from '../../../components/Button.tsx';
import { Checkbox } from '../../../components/Field.tsx';
import { useNow } from '../../../components/useNow.ts';
import { Timestamp } from '../../../components/Timestamp.tsx';
import styles from './NewRobotDialog.module.css';

/* ==========================================================================
   THE ONE-TIME ENROLMENT TOKEN

   The server mints this, hashes it, stores the hash and returns the raw value
   exactly once (PRD-0001 §5). The robot trades it, with a CSR, at
   POST /api/enroll/{robot_id} for the X.509 identity it will authenticate
   every cloud call with from then on. There is no "show it again": if this
   dialog closes with the token uncopied, the only recovery is minting a new
   one, which invalidates this one.

   So the surface is built around four facts, in this order of loudness:

     1. IT IS SHOWN ONCE. Stated in the block that holds it, and enforced —
        dismissing an uncopied token takes a second, deliberate action. Escape
        still always works; it just asks once first.
     2. IT IS A SECRET. Masked by default, because the operator issuing it is
        as likely to be on a shared screen as not, and copy works without
        revealing. The mask is the token's true length, so nothing is implied
        about it that is not true.
     3. IT EXPIRES. A live countdown against the server's `expires_at`, plus
        the absolute Zulu deadline, plus a depletion bar. The clock is the one
        thing on this surface allowed to change colour. Past the deadline the
        surface stops selling the workflow: the eyebrow reads EXPIRED · CANNOT
        ENROL, Copy stops being the primary verb, the sequence is marked dead,
        and the dismissal guard stands down — there is nothing left to lose.
     4. WHAT TO DO WITH IT. Three steps, then the exact block to paste — the
        real bringup script from the pinned revision, numbered, unwrapped, with
        the digest the agent verifies printed in the pane's header.

   The token is deliberately NOT in the pasted block. The script prompts for it
   from /dev/tty, because a one-time credential on a command line lands in
   shell history and is visible in `ps` for the life of the process.
   ========================================================================== */

type Kind = 'harness' | 'comment' | 'code' | 'blank';

interface CodeLine {
  n: number;
  text: string;
  kind: Kind;
}

/* Classify each line of the pasted block so the operator can tell the script
   they are about to run as root from the three lines this console wrapped
   around it. Harness indices are structural, not guessed from the text:
   buildInstallBlock emits [heredoc open] · script · [delimiter] · [run]. */
function classify(block: string, scriptLineCount: number): CodeLine[] {
  const raw = block.replace(/\n+$/, '').split('\n');
  const delimiterAt = scriptLineCount + 1;
  return raw.map((text, i) => {
    const kind: Kind =
      i === 0 || i === delimiterAt || i === delimiterAt + 1 ? 'harness'
        : text.trim() === '' ? 'blank'
          : text.trimStart().startsWith('#') && !text.trimStart().startsWith('#!') ? 'comment'
            : 'code';
    return { n: i + 1, text, kind };
  });
}

/** `29:41`, `4:03`, `0:07` — mm:ss, tabular, never a rounded "about 5 minutes". */
function mmss(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function EnrollmentTokenDialog({
  robotId,
  token,
  expiresAt,
  note,
  onClose,
}: {
  robotId: string;
  token: string;
  expiresAt?: string | undefined;
  /** The server's own words about the token, from the mint response. */
  note?: string | undefined;
  onClose: () => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [acked, setAcked] = useState(false);
  const apiBase = window.location.origin;

  /* The token's window. The dialog opens on the mutation's success callback,
     so first render IS the mint — which makes `issued → expires` a measured
     span rather than an assumed TTL. Held in a ref so a re-render cannot
     restart the clock. */
  const issuedAt = useRef(Date.now());
  const now = useNow(true);
  const expiresMs = expiresAt === undefined ? NaN : Date.parse(expiresAt);
  const hasDeadline = Number.isFinite(expiresMs);
  const remainMs = hasDeadline ? expiresMs - now : NaN;
  const windowMs = hasDeadline ? Math.max(1, expiresMs - issuedAt.current) : 1;
  const expired = hasDeadline && remainMs <= 0;
  const urgent = hasDeadline && !expired && remainMs < 120_000;
  const tone = expired ? 'error' : 'warn';

  /* THE SCRIPT COMES FROM THE REVISION, never from a copy bundled into the
     console: it is the same text the Catalog editor saved and the same the
     robot would fetch from script_url, so an operator's edit is what gets
     pasted rather than a plausible-looking substitute. */
  const { data: revs } = useRobotRevisions(robotId);
  const revision = revs?.revisions.find((r) => r.id === revs.current);
  const { data: bringup, isLoading: scriptLoading } = useBringupContent(
    revs?.morphology,
    revision?.version,
  );
  const script = bringup?.content?.trim() ? bringup.content : null;

  /* NO FALLBACK when the revision has no stored script. A revision without one
     is not bringup_ready, so /api/enroll returns no bringup block and the run
     would fail at the payload step anyway; handing over an invented script
     would only move the failure later and make it look like the script's. */
  const installBlock = useMemo(
    () => (script === null ? null : buildInstallBlock({ script, robotId, apiBase })),
    [script, robotId, apiBase],
  );
  const scriptLineCount = script === null
    ? 0
    : script.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n').length;
  const lines = useMemo(
    () => (installBlock === null ? [] : classify(installBlock, scriptLineCount)),
    [installBlock, scriptLineCount],
  );
  /* The countdown re-renders this component every second. Holding the script's
     elements by reference lets React skip the whole 40-line subtree on each
     tick — the clock moves, nothing else does. */
  const codeBody = useMemo(
    () => lines.map((l) => (
      <div key={l.n} className={styles.codeLine} data-kind={l.kind}>
        <span className={styles.codeNum}>{l.n}</span>
        <span className={styles.codeText}>{l.text === '' ? ' ' : l.text}</span>
      </div>
    )),
    [lines],
  );

  /* Secured = the operator has the token, or has said so. Until then a
     dismissal is a two-step action — but the SECOND press always works. A
     dialog that cannot be closed is a worse defect than a lost token. Same
     guard the register and edit forms use; here what it protects is not a
     form's worth of typing but a credential with no second showing. */
  const secured = copied || acked;
  /* An expired token is not worth guarding: there is nothing left to lose, and
     a dialog that argues about being closed after the credential it holds has
     already died is a dialog that has stopped tracking its own state. */
  const guard = useDismissGuard(!secured && !expired, onClose);
  const { armed, dismiss } = guard;

  const digest = bringup?.script_sha256 ?? null;

  return (
    <Dialog
      title="Enrolment token"
      subject={robotId}
      /* The exact URI, not the route template. These four dialogs print the
         call they are about to make; on a surface whose whole subject is one
         named machine, `{id}` is the one part the operator cannot verify. */
      endpoint={`POST /api/robots/${robotId}/enrollment-token`}
      width="lg"
      labelledBy="enrol-title"
      initialFocus="#enrol-copy"
      onDismiss={dismiss}
      dismissHint={guard.hint}
      footLead={
        <span
          className={styles.live}
          aria-live="assertive"
          {...(armed || expired ? { 'data-tone': 'error' } : {})}
        >
          {armed
            ? 'Not copied. Closing discards it — the API cannot show it again.'
            : expired
              ? 'Expired. Re-issue a token from the robot’s ⋮ menu.'
              : copied
                ? 'Copied to the clipboard.'
                : acked
                  ? 'Marked as stored.'
                  : 'Copy the token before closing.'}
        </span>
      }
      footEnd={
        <>
          {/* Keyed on `copied`, not on `secured`: ticking this box is what
              makes the dialog secure, and unmounting the control the operator
              just operated drops focus onto <body> — after which Escape does
              nothing. Copying instead leaves focus on the Copy button, which
              stays put, so that path is safe to unmount on. */}
          {!copied && !expired && (
            <Checkbox
              id="enrol-ack"
              label="I have stored it"
              checked={acked}
              onChange={(e) => setAcked(e.target.checked)}
            />
          )}
          {/* The verb SAYS what the next press does. A close button whose
              first press only changes a line of status text is a button that
              looks broken; armed, it becomes the destructive verb it is. */}
          <Button
            variant={secured || expired ? 'secondary' : armed ? 'danger' : 'ghost'}
            {...(armed ? { icon: 'alert-triangle' as const } : {})}
            onClick={dismiss}
            title={
              secured
                ? 'Close'
                : armed
                  ? 'Discards a token the API cannot show again'
                  : 'Close without copying the token'
            }
          >
            {secured || expired ? 'Done' : armed ? 'Discard anyway' : 'Close'}
          </Button>
        </>
      }
    >
      {/* ---- 1 · the secret ------------------------------------------------ */}
      <div className={styles.secret} data-tone={tone}>
        <div className={styles.secretHead}>
          <span className={styles.secretLabel}>
            {expired ? 'Expired · cannot enrol' : 'Shown once · not recoverable'}
          </span>
          <span
            className={styles.secretClock}
            {...(urgent ? { 'data-urgent': 'true' } : {})}
          >
            {!hasDeadline
              ? 'no deadline returned'
              : expired
                ? `expired ${mmss(-remainMs)} ago`
                : `expires in ${mmss(remainMs)}`}
          </span>
        </div>

        <div className={styles.secretRow}>
          <span className={styles.secretField}>
            {revealed
              ? token
              : <span className={styles.secretMask} aria-hidden="true">{'•'.repeat(token.length)}</span>}
          </span>
          <IconButton
            icon={revealed ? 'eye-off' : 'eye'}
            label={revealed ? 'Hide the token' : 'Reveal the token on screen'}
            onClick={() => setRevealed((v) => !v)}
          />
          {/* Still copyable after expiry — an operator may want the value for
              a log — but no longer the surface's primary verb, because it can
              no longer enrol anything. */}
          <CopyButton
            id="enrol-copy"
            value={token}
            label="Copy token"
            variant={expired ? 'secondary' : 'primary'}
            onCopied={() => setCopied(true)}
          />
        </div>

        {/* Remaining life as a measured fraction of the window this dialog has
            watched, not of an assumed TTL. */}
        {hasDeadline && (
          <div
            className={styles.life}
            role="img"
            aria-label={expired ? 'Token expired' : `${mmss(remainMs)} of the token's life remaining`}
          >
            <span
              className={styles.lifeFill}
              style={{ width: `${Math.max(0, Math.min(100, (remainMs / windowMs) * 100))}%` }}
            />
          </div>
        )}

        <div className={styles.secretFoot}>
          <span>
            {note ?? `${token.length} chars, base64url. The API stored only its SHA-256; re-issuing invalidates this one.`}
          </span>
          {expiresAt !== undefined && (
            <span className={styles.secretFootTime}>
              <Timestamp value={expiresAt} mode="utc" />
            </span>
          )}
        </div>
      </div>

      {/* ---- 2 · the sequence ---------------------------------------------- */}
      <DialogSection
        label="Sequence"
        note={expired
          ? 'dead — re-issue before running this'
          : 'expires on first enrol, or at the deadline above'}
        {...(expired ? { tone: 'error' } : {})}
      >
        <ol className={styles.steps}>
          <li className={styles.step} data-done={copied}>
            <span className={styles.stepNum}>{copied ? '✓' : '1'}</span>
            Copy the token
            <span className={styles.stepWhere}>here, once</span>
          </li>
          <li className={styles.step}>
            <span className={styles.stepNum}>2</span>
            Paste the block below; it prompts for the token
            <span className={styles.stepWhere}>root shell on {robotId}</span>
          </li>
          <li className={styles.step}>
            <span className={styles.stepNum}>3</span>
            The robot posts a CSR and receives its certificate
            <span className={styles.stepWhere}>
              POST {apiBase}/api/enroll/{robotId}
            </span>
          </li>
        </ol>
      </DialogSection>

      {/* ---- 3 · the bring-up block ---------------------------------------- */}
      {installBlock !== null ? (
        <DialogSection
          label="Bring-up"
          note="one paste — writes the script, then runs it"
        >
          <div className={styles.code}>
            <div className={styles.codeHead}>
              <span className={styles.codeName}>bringup.sh</span>
              <span className={styles.codeMeta}>
                {revs?.morphology} {revision?.version} · {scriptLineCount} lines
                {digest !== null && ` · sha256 ${digest.slice(0, 12)}`}
              </span>
              <span className={styles.codeSpacer} />
              <CopyButton value={installBlock} label="Copy block" size="sm" />
            </div>
            <pre className={styles.codePane}>{codeBody}</pre>
          </div>
          {/* The three accented lines are this console's, not the revision's. */}
          <p className={styles.note}>
            Highlighted lines are this console&apos;s wrapper; the rest is{' '}
            <code>{revs?.morphology} {revision?.version}</code> verbatim. The block holds
            no token: the script prompts for it on <code>/dev/tty</code>, keeping it out
            of shell history and <code>ps</code>.
          </p>
        </DialogSection>
      ) : (
        <DialogSection
          label="Bring-up"
          note={scriptLoading ? 'reading catalog…' : 'no script stored'}
          {...(scriptLoading ? {} : { tone: 'warn' })}
        >
          {/* The pane keeps its header and its shape; the absence is one line
              where the script would be, not an alarm competing with the token
              above it. */}
          <div className={styles.code}>
            <div className={styles.codeHead}>
              <span className={styles.codeName}>bringup.sh</span>
              <span className={styles.codeMeta}>
                {revision === undefined
                  ? 'no revision pinned'
                  : `${revs?.morphology} ${revision.version} · not stored`}
              </span>
            </div>
            <p className={styles.codeEmpty}>
              {scriptLoading
                ? 'Fetching the pinned revision’s bring-up script…'
                : revision === undefined
                  ? 'No hardware revision is pinned, so there is nothing to install. Pin one on Configuration, then re-issue a token.'
                  : (
                    <>
                      Revision <code>{revision.version}</code> carries no bring-up
                      script, so it cannot provision a robot. Add one in Catalog →{' '}
                      <code>{revs?.morphology}</code> → <code>{revision.version}</code>,
                      then re-issue a token.
                    </>
                  )}
            </p>
          </div>
        </DialogSection>
      )}
    </Dialog>
  );
}
